import { Card, FormGroup } from '@ksyos/design-system';
import { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { IAddServiceActivity } from '@common/interfaces';
import { SettingsCanWrite } from '@frontend/components/pages/carePath/modals/components/SettingsCanWrite';
import type { EpisodeCategory } from '../../../../../../common/enums/episodeCategory';
import type { IAddableServiceGroupServiceRights } from '../../../../../../common/interfaces/addableServiceGroupServiceRights';
import type { IService } from '../../../../../../common/interfaces/service';
import type { ISelectableOption } from '../../../../interfaces/selectableOption';
import type { ISelectableCard } from '../../../organizations/organizationAddCarePath/OrganizationAddCarePathContent';
import { translateKey } from '../../../../../translate';
import { SelectableOptionsCard } from '../../../carePath/cards/SelectableOptionsCard';
import { CareProviderAddRightsContext } from '../careProviderAddRightsContext';

interface IChooseRightsProps {
    addableServiceGroupServiceRights: IAddableServiceGroupServiceRights;
}

export interface IServiceRight {
    id: string;
    service: IService;
}

export interface IServiceActivityRight {
    activity: IAddServiceActivity;
    id: string;
}

interface IServiceReaderRight {
    episodeCategory: EpisodeCategory;
    id: string;
}

interface IServiceRights {
    consumerServices: IServiceRight[];
    providerServices: IServiceRight[];
    supervisorServices: IServiceRight[];
}

export function ChooseRights(props: IChooseRightsProps) {
    const { addRightsState, dispatchAddRightsAction } = useContext(CareProviderAddRightsContext);
    const [card, setCard] = useState<ISelectableCard>();
    const [serviceRights, setServiceRights] = useState<IServiceRights>({
        consumerServices: [],
        providerServices: [],
        supervisorServices: [],
    });
    const [serviceActivities, setServiceActivities] = useState<IServiceActivityRight[]>([]);
    const [serviceReaderRight, setServiceReaderRight] = useState<IServiceReaderRight | undefined>();

    useEffect(() => {
        prepareServiceRights();
        prepareServiceActivities();
    }, [props.addableServiceGroupServiceRights]);

    useEffect(() => {
        createChooseRightsCard();
    }, [
        serviceRights,
        serviceReaderRight,
        serviceActivities,
        (addRightsState.consumerServices ?? []).length,
        (addRightsState.providerServices ?? []).length,
        (addRightsState.supervisorServices ?? []).length,
        addRightsState.serviceEmploymentActivities,
        addRightsState.addServiceReader,
    ]);

    if (!card) {
        return null;
    }

    if (serviceActivities.length > 0) {
        return (
            <Card>
                <Card.Header title={translateKey('addRights.whichRightsToAdd')} />
                <Card.Body>
                    {serviceActivities.map((service) => (
                        <FormGroup
                            key={service.activity.id}
                            label={service.activity.name}
                        >
                            <SettingsCanWrite
                                canWrite={service.activity.canWrite}
                                enabled={isActivitySelected(service)}
                                onChange={(canWrite, enabled) => onChange(service.activity.id, canWrite, enabled)}
                            />
                        </FormGroup>
                    ))}
                </Card.Body>
            </Card>
        );
    }

    return (
        <SelectableOptionsCard
            {...card}
            onChange={(id) => onChange(id)}
        />
    );

    function onChange(id: string, canWrite: boolean = false, enabled: boolean = false) {
        const serviceEmploymentRight = serviceActivities.find((right) => right.activity.id === id);
        if (serviceEmploymentRight) {
            return dispatchAddRightsAction({
                type: 'setServiceEmploymentActivity',
                activity: { ...serviceEmploymentRight, activity: { ...serviceEmploymentRight.activity, canWrite } },
                enabled,
            });
        }

        let serviceRight = serviceRights.consumerServices.find((right) => right.id === id);

        if (serviceRight) {
            return dispatchAddRightsAction({ type: 'setServiceConsumer', service: serviceRight.service });
        }
        serviceRight = serviceRights.providerServices.find((right) => right.id === id);
        if (serviceRight) {
            return dispatchAddRightsAction({ type: 'setServiceProvider', service: serviceRight.service });
        }
        serviceRight = serviceRights.supervisorServices.find((right) => right.id === id);
        if (serviceRight) {
            return dispatchAddRightsAction({ type: 'setServiceSupervisor', service: serviceRight.service });
        }

        if (id === serviceReaderRight?.id) {
            dispatchAddRightsAction({ type: 'setServiceReader' });
        }
    }

    function createChooseRightsCard() {
        const selectableOptions: ISelectableOption[] = [
            ...getSelectableOptions(serviceRights.consumerServices, 'consumer'),
            ...getSelectableOptions(serviceRights.providerServices, 'provider'),
            ...getSelectableOptions(serviceRights.supervisorServices, 'supervisor'),
            ...getSelectableActivityOptions(serviceActivities),
        ].sort(sortOptions);

        const serviceReaderSelectableOption = getServiceReaderSelectableOption();
        if (serviceReaderSelectableOption) {
            selectableOptions.push(serviceReaderSelectableOption);
        }

        if (selectableOptions.length > 0) {
            const chooseRightsCard: ISelectableCard = {
                title: translateKey('addRights.whichRightsToAdd'),
                subtitle: translateKey('carePath.multipleChoicesPossible'),
                sequence: 0,
                selectableOptions,
            };

            setCard(chooseRightsCard);
        }
    }

    function sortOptions(option1: ISelectableOption, option2: ISelectableOption) {
        const result = option1.name.localeCompare(option2.name);
        if (result === 0) {
            return option1.displayName.localeCompare(option2.displayName);
        } else {
            return result;
        }
    }

    function getSelectableOptions(services: IServiceRight[], rightType: 'consumer' | 'provider' | 'supervisor') {
        return services.map((serviceRight) => {
            let selected = false;
            switch (rightType) {
                case 'consumer':
                    selected = Boolean(
                        addRightsState.consumerServices?.some((service) => service.id === serviceRight.service.id)
                    );
                    break;
                case 'provider':
                    selected = Boolean(
                        addRightsState.providerServices?.some((service) => service.id === serviceRight.service.id)
                    );
                    break;
                case 'supervisor':
                    selected = Boolean(
                        addRightsState.supervisorServices?.some((service) => service.id === serviceRight.service.id)
                    );
                    break;

                default:
                    break;
            }

            return {
                name: serviceRight.service.name,
                displayName: getRightLabel(serviceRight.service.name, rightType),
                id: serviceRight.id,
                selected,
            };
        });
    }

    function getSelectableActivityOptions(activities: IServiceActivityRight[]) {
        return activities.map((serviceActivityRight) => {
            return {
                name: serviceActivityRight.activity.name,
                displayName: getRightLabel(serviceActivityRight.activity.name, 'activity'),
                id: serviceActivityRight.activity.id,
                serviceId: serviceActivityRight.id,
                selected: isActivitySelected(serviceActivityRight),
            };
        });
    }

    function isActivitySelected(serviceActivityRight: IServiceActivityRight) {
        return (
            addRightsState.serviceEmploymentActivities
                .filter((service) => service.id === serviceActivityRight.id)[0]
                ?.serviceActivities.some(
                    (serviceActivity) => serviceActivity.id === serviceActivityRight.activity.id
                ) || false
        );
    }

    function getServiceReaderSelectableOption() {
        if (serviceReaderRight) {
            const displayName = `${translateKey('addCarePathSectionTitles.serviceReaderSettings')} ${translateKey(
                `episodeCategoryEnum.${serviceReaderRight.episodeCategory}`
            )}`;

            return {
                name: serviceReaderRight.episodeCategory as string,
                displayName,
                id: serviceReaderRight.id,
                selected: addRightsState.addServiceReader ?? false,
            };
        }
    }

    function prepareServiceRights() {
        const preparedServiceRights: IServiceRights = {
            consumerServices: prepareServiceRightServices(
                props.addableServiceGroupServiceRights.serviceConsumerServices
            ),
            providerServices: prepareServiceRightServices(
                props.addableServiceGroupServiceRights.serviceProviderServices
            ),
            supervisorServices: prepareServiceRightServices(
                props.addableServiceGroupServiceRights.serviceSupervisorServices
            ),
        };
        setServiceRights(preparedServiceRights);

        if (props.addableServiceGroupServiceRights.isServiceReaderAddable) {
            const preparedServiceReaderRight = {
                id: uuidv4(),
                episodeCategory: props.addableServiceGroupServiceRights.episodeCategory,
            };
            setServiceReaderRight(preparedServiceReaderRight);
        }
    }

    function prepareServiceActivities() {
        const preparedServiceActivities: Array<IServiceActivityRight> = [];

        props.addableServiceGroupServiceRights.serviceEmploymentActivities.forEach((serviceWithActivities) => {
            serviceWithActivities.serviceActivities.forEach((activity) => {
                preparedServiceActivities.push({
                    id: serviceWithActivities.id,
                    activity,
                });
            });
        });

        setServiceActivities(preparedServiceActivities);
    }

    function prepareServiceRightServices(services: IService[]) {
        return services.map((service) => ({
            id: uuidv4(),
            service,
        }));
    }

    function getRightLabel(serviceRightName: string, rightType: 'consumer' | 'provider' | 'supervisor' | 'activity') {
        if (rightType === 'activity') {
            return translateKey('addCarePathSettingsLabelsServiceEmployment.canPerform', serviceRightName);
        } else {
            return translateKey(`addRights.${rightType}`, serviceRightName);
        }
    }
}
